import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { cloneDeep, orderBy, isEmpty, round, uniqBy } from 'lodash';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as action from '../../../store/DevelopmentOrder.action';
import SpecificationsView from './SpecificationsView';
import { checkArray } from '../../../../../Components/helper';
import { useComponent } from '../../../../UserSetting/UsersLibrary/UserRights';
import { toasterMsg } from '../../../../../Components/Toaster/Toaster';
import { searchGradingGroupPoms } from '../../../../../services/sample.service';
import { getAllPom } from '../../../../Library/Pom/Store/Pom.action';
import DragDropSort from '../../../../../Components/DragDropSort/DragDropSort';
import CustomTooltip from '../../../../../Components/Tooltip/CustomTooltip';
import { deriveSizeVisibility } from '../../../Measurment/sampleSizeUtils';

const Specifications = forwardRef((props, ref) => {
  const { t } = useTranslation('common');
  const { t: tPS } = useTranslation('product-sample');

  const { SampleDeleteAccess, SampleAddEditAccess, devOrderId, onManualPomPendingChange } = props;
  const valueRef = useRef();
  const originalSpecificationDataRef = useRef([]);
  const dispatch = useDispatch();
  const devOrderReducer = useSelector((reducerstate) => reducerstate);
  const {
    Measuremets,
    UserDetails,
    BOMConfigDetails: { productDetails },
    Users: { logInRights },
  } = devOrderReducer;
  const { CheckActionPermission } = useComponent();
  const measurementAddEditRights = CheckActionPermission(logInRights, 'products', 'measurement', 'addEdit');
  const { sampleDetails, sampleSpecificationReducer, sampleData } = Measuremets;
  const { isSupplierLoggedIn } = UserDetails;
  const queryParams = useLocation();
  const [userChangedIndexes, setUserChangedIndexes] = useState([]);
  const { search } = queryParams;
  const smapleId = new URLSearchParams(search).get('sample');
  const [state, setState] = useState({
    sampleSpecificationList: [],
    deleteId: null,
    pageIndex: 1,
    pageSize: 20,
    hasMore: true,
    loading: true,
    totalRecords: null,
    sortOrder: '',
    sortPropertyName: '',
    propertName: '',
    columnSortValue: 'asc',
    conversationType: '',
    fraction: '',
  });
  const [valueSelect, setValueSelect] = useState({});
  const [selectedGradGroup, setSelectedGradGroup] = useState({});
  const [gradingGroupOptions, setGradingGroupOptions] = useState([]);

  const [selectedSize, setSelectedSize] = useState({});
  const [sizeRange, setSizeRange] = useState([]);
  const [lengthEnabled, setLengthEnabled] = useState(false);
  const [hiddenSizeNames, setHiddenSizeNames] = useState([]);
  const [importSource, setImportSource] = useState({
    id: 'POM Library',
    value: 'POM Library',
    label: (
      <CustomTooltip placement="right" tooltipText={t('MEASUREMENT.POM_LIBRARY')}>
        <i className="icon-Inside-Measurements f-24" />
      </CustomTooltip>
    ),
  });
  const [importSearchResults, setImportSearchResults] = useState([]);
  const [importLoader, setImportLoader] = useState(false);
  const typeaheadRef = useRef(null);

  /**
   * Deduplicated POM list (one entry per unique code) for DragDropSort. Sort order mirrors
   * SpecificationsView's grouped table sort so the modal sequence matches what the user sees
   * on screen. Sequence is re-stamped to the sorted index because DragDropSort re-applies
   * `orderBy('sequence', 'asc')` to its input, which would otherwise push manual POMs with
   * missing/zero sequence to the top.
   */
  const pomItemsForRearrange = useMemo(() => {
    const seen = new Set();
    const unique = (state.sampleSpecificationList || []).filter((item) => {
      const key = item.tempId || item.code;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    const ADDED_TYPES = new Set(['pomLibrary', 'gradingGroup', 'manual']);
    return unique
      .slice()
      .sort((a, b) => {
        if (!!a.tempId !== !!b.tempId) return a.tempId ? 1 : -1;
        const aSeq = a.sequence ?? 0;
        const bSeq = b.sequence ?? 0;
        if (aSeq !== bSeq) return aSeq - bSeq;
        const aAdded = ADDED_TYPES.has(a.sourceType) ? 1 : 0;
        const bAdded = ADDED_TYPES.has(b.sourceType) ? 1 : 0;
        if (aAdded !== bAdded) return aAdded - bAdded;
        return (a.code || '').localeCompare(b.code || '');
      })
      .map((item, idx) => ({ ...item, sequence: idx + 1 }));
  }, [state.sampleSpecificationList]);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('loggedInUserInfo'));
    if (userData?.lengthVisible !== null) {
      setLengthEnabled(userData?.lengthVisible);
    }
  }, []);

  useEffect(() => {
    const hasUnsavedManualPom = (state.sampleSpecificationList || []).some((item) => !!item.tempId);
    onManualPomPendingChange?.(hasUnsavedManualPom);
  }, [state.sampleSpecificationList, onManualPomPendingChange]);

  useEffect(() => {
    return () => {
      onManualPomPendingChange?.(false);
    };
  }, [onManualPomPendingChange]);

  const headerSortingIcon = (lable, propertyName) => {
    return (
      <span className="d-flex align-items-center cursor-pointer">
        <span className="mb-0 flex-fill" style={{ flex: 0 }}>
          {lable}
        </span>
        {state?.propertName === propertyName && state?.columnSortValue === '' && <i className="icon-Table-Up font-weight-heavy text-black f-14 ml-1" />}
        {state?.propertName === propertyName && state?.columnSortValue === 'desc' && <i className="icon-Table-Down font-weight-heavy text-black f-14 ml-1" />}
        {state?.propertName === propertyName && state?.columnSortValue === 'asc' && <i className="icon-Table-Up font-weight-heavy text-black f-14 ml-1" />}
        {state?.propertName !== propertyName && <i className="icon-Table-Up font-weight-heavy text-black f-14 ml-1" />}
      </span>
    );
  };

  const handleAddEmptyRow = () => {
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const emptyRows = sizeRange.map((s) => ({
      tempId,
      code: '',
      description: '',
      tolMinus: '',
      tolPlus: '',
      sizeName: s.size || s.label.split(',')[0].trim(),
      requestedMeasurement: '',
      supplierMeasurement: '',
      actualMeasurement: '',
      revisedMeasurement: '',
      discrepancy: '',
      discrepancyColor: null,
      sourceType: 'manual',
      isZero: s.isSampleSize || false,
    }));
    setState((prev) => ({
      ...prev,
      sampleSpecificationList: [...prev.sampleSpecificationList, ...emptyRows],
    }));
  };

  useImperativeHandle(ref, () => ({
    getSampleSpecification() {
      return {
        sampleSpecificationList: state.sampleSpecificationList,
        conversationType: state.conversationType,
        fraction: state.fraction,
        sizeName: selectedSize,
        selectedGradGroup,
      };
    },
    getUserChangedIndexes() {
      return {
        userChangedIndexes,
      };
    },
    addEmptyPomRow() {
      handleAddEmptyRow();
    },
    hasPendingChanges() {
      const currentData = state.sampleSpecificationList;
      const originalData = originalSpecificationDataRef.current;

      if (!currentData || !originalData) return false;
      if (currentData.length !== originalData.length) return false;

      return currentData.some((item, index) => {
        const originalItem = originalData[index];
        if (!originalItem) return false;

        return (
          item?.supplierMeasurement !== originalItem?.supplierMeasurement ||
          item?.actualMeasurement !== originalItem?.actualMeasurement ||
          item?.revisedMeasurement !== originalItem?.revisedMeasurement ||
          item?.comments !== originalItem?.comments
        );
      });
    },
  }));
  useEffect(() => {
    const Init = async () => {
      setState((prevState) => ({
        ...prevState,
        loading: true,
      }));

      setState((prevState) => ({
        ...prevState,
        loading: false,
      }));
    };
    Init();
  }, [dispatch, productDetails?.gradingGroups?.length]);

  useEffect(() => {
    if (sampleDetails?.gradingGroupData?.length > 0) {
      let gradingOptions = sampleDetails.gradingGroupData
        .filter((ele) => ele?.gradingGroup?._id && ele?.size)
        .map((ele) => ({
          value: ele?.gradingGroup?._id,
          label: ele?.gradingGroup?.name,
          sizeRanges: ele?.sizeRanges,
          conversationType: ele?.gradingGroup?.conversationType,
          fraction: ele?.gradingGroup?.fraction,
        }));
      gradingOptions = uniqBy(gradingOptions, 'value');
      setGradingGroupOptions(gradingOptions);
    }
  }, [sampleDetails?.gradingGroupData]);

  // Restore grad group selection and reload spec data when re-mounting after switching back from Supplier tab
  useEffect(() => {
    if (props.selectedGradGroup?.value && !selectedGradGroup?.value) {
      const gradGroup = props.selectedGradGroup;
      setSelectedGradGroup(gradGroup);
      setState((prev) => ({ ...prev, loading: true, conversationType: gradGroup?.conversationType, fraction: gradGroup?.fraction }));
      dispatch(
        action.getSampleSpecificationByGradingGroup({
          sampleId: smapleId,
          gradingGroupId: gradGroup.value,
        }),
      ).finally(() => setState((prev) => ({ ...prev, loading: false })));
    }
  }, [props.selectedGradGroup?.value]);

  const buildSizeEntry = (ele, length) => {
    const size = ele?.size;
    const gradingGroup = ele?.gradingGroup?._id;
    const quan = ele?.quantity ? `(${ele?.quantity})` : ' ';
    if (!size) return null;
    const label = length ? `${size}, ${length} ${quan}` : `${size} ${quan}`;
    return { value: ele._id, label, size, gradingGroup, isSampleSize: ele?.isSampleSize || false };
  };

  useEffect(() => {
    if (!selectedGradGroup?.value) return;
    const values = (sampleData?.gradingGroupData || [])
      .map((ele) => buildSizeEntry(ele, lengthEnabled ? ele?.len?.name : null))
      .filter((v) => v && v.gradingGroup === selectedGradGroup?.value);

    const visibilityData = deriveSizeVisibility({
      selected: values,
      protectedSampleSizes: [],
      sizeOptions: values,
      matchingSizeRange: sampleData?.matchingSizeRange,
      localSizeRanges: [],
    });

    setHiddenSizeNames(visibilityData.hiddenSizeNames);

    const visibleSizes = values.filter((size) => !visibilityData.hiddenSizeNames.includes(size.size));
    setSizeRange(visibleSizes);
  }, [selectedGradGroup, sampleData, lengthEnabled]);

  const setDataForConversionDiscrepancy = (specificationDeepDataArg) => {
    const modifiedData = specificationDeepDataArg || [];
    // CHECK TYPE NOT CM THEN CONVERT IT INTO INCH AND THEN FRACTION
    // CALCULATE DISCREPANCY & SET DATA
    if (modifiedData && modifiedData.length > 0) {
      modifiedData.forEach((item, i) => {
        modifiedData[i].actualMeasurement = item.actualMeasurement ? setNumberTo4decimal(item.actualMeasurement) : 0;
        modifiedData[i].requestedMeasurement = item.requestedMeasurement ? setNumberTo4decimal(item.requestedMeasurement) : 0;
        modifiedData[i].supplierMeasurement = item.supplierMeasurement ? setNumberTo4decimal(item.supplierMeasurement) : 0;
        modifiedData[i].tolMinus = item.tolMinus ? setNumberTo4decimal(item.tolMinus) : 0;
        modifiedData[i].tolPlus = item.tolPlus ? setNumberTo4decimal(item.tolPlus) : 0;
        modifiedData[i].revisedMeasurement = item.revisedMeasurement ? setNumberTo4decimal(item.revisedMeasurement) : 0;
        const discrepancy = modifiedData[i] ? modifiedData[i].actualMeasurement - modifiedData[i].requestedMeasurement : '';
        const ifHasDiscrepancy = discrepancy && discrepancy > 0 ? `+${round(discrepancy, 4)}` : round(discrepancy, 4);
        modifiedData[i].discrepancy = Number.isNaN(discrepancy) ? 0 : ifHasDiscrepancy;
        const discrColor = getDiscrepancyColor(modifiedData[i].actualMeasurement, modifiedData[i].requestedMeasurement, modifiedData[i].tolMinus);
        modifiedData[i].discrepancyColor = discrColor ? 'green' : 'red';
      });
    }

    const visibleMeasurements = modifiedData.filter((item) => !hiddenSizeNames.includes(item.sizeName));
    if (!isEmpty(modifiedData)) {
      setState((prevState) => ({
        ...prevState,
        sampleSpecificationList: visibleMeasurements,
      }));
      originalSpecificationDataRef.current = cloneDeep(modifiedData);
    }
  };

  useEffect(() => {
    if (checkArray(sampleSpecificationReducer)) {
      const specificationDeepData = cloneDeep(sampleSpecificationReducer);
      const hasUnsavedChanges = valueRef.current?.hasPendingChanges?.();
      if (!hasUnsavedChanges) {
        setDataForConversionDiscrepancy(specificationDeepData);
      }
    } else {
      setState((prevState) => ({
        ...prevState,
        sampleSpecificationList: [],
      }));
    }
  }, [sampleSpecificationReducer]);

  useEffect(() => {
    if (state.sampleSpecificationList && state.sampleSpecificationList.length > 0 && hiddenSizeNames.length > 0) {
      const visibleMeasurements = state.sampleSpecificationList.filter((item) => !hiddenSizeNames.includes(item.sizeName));
      setState((prevState) => ({
        ...prevState,
        sampleSpecificationList: visibleMeasurements,
      }));
    }
  }, [hiddenSizeNames]);

  function setNumberTo4decimal(input) {
    const allowedPlaceholders = new Set(['/', 'N/A', 'NA', '-', 'N', 'A']);
    const normalizedInput = String(input).toUpperCase();
    if (allowedPlaceholders.has(normalizedInput)) {
      return input;
    }
    const numberValue = Number(input);
    const isValidNumber = !Number.isNaN(numberValue) && Number.isFinite(numberValue);
    return isValidNumber ? parseFloat(numberValue.toFixed(4)) : 0;
  }

  const onSortColumns = (key, columnSortValue) => {
    let sampleSpecification = state.sampleSpecificationList;

    let columnSortVal = columnSortValue;
    if (state.propertName !== key) {
      columnSortVal = 'asc';
    } else if (columnSortVal === 'asc') {
      columnSortVal = 'desc';
    } else if (columnSortVal === 'desc') {
      columnSortVal = 'asc';
    }
    sampleSpecification = orderBy(
      sampleSpecification,
      (o) => {
        if (!Number.isNaN(Number(o[key]))) {
          return +o[key];
        }
        return o[key];
      },
      [columnSortVal],
    );
    setState((prevState) => ({
      ...prevState,
      propertName: key,
      columnSortValue: columnSortVal,
      sampleSpecificationList: sampleSpecification,
    }));
  };

  const callDiffFunction = async (pIndex, frac, field) => {
    const updatedFrac = { ...frac }; // Create a copy of 'frac'

    updatedFrac[field] = true;

    if (updatedFrac.mainChange && updatedFrac.numChange && updatedFrac.dnumChange) {
      setDiscrepancy(pIndex);
      updatedFrac.mainChange = false;
      updatedFrac.numChange = false;
      updatedFrac.dnumChange = false;
    }
  };

  const setDiscrepancy = async (index) => {
    const cloneStat = cloneDeep(state.sampleSpecificationList);
    let reqMeasurement = cloneStat[index].requestedMeasurement;
    let actMeasurement = cloneStat[index].actualMeasurement;
    let tol = cloneStat[index].tolMinus;

    actMeasurement = actMeasurement ? setNumberTo4decimal(actMeasurement) : 0;
    reqMeasurement = reqMeasurement ? setNumberTo4decimal(reqMeasurement) : 0;
    tol = tol ? setNumberTo4decimal(tol) : 0;

    const discrepancyColor = await getDiscrepancyColor(actMeasurement, reqMeasurement, tol);
    cloneStat[index].discrepancy = setNumberTo4decimal(actMeasurement - reqMeasurement);
    cloneStat[index].discrepancyColor = discrepancyColor ? 'green' : 'red';
    setState((prevState) => ({
      ...prevState,
      sampleSpecificationList: cloneStat,
    }));
  };

  const getDiscrepancyColor = (actMeasurement, reqMeasurement, tol) => {
    const reqMaxMeasurement = reqMeasurement + tol;
    const reqMinMeasurement = reqMeasurement - tol;
    let discrepancyColor = null;

    if (actMeasurement >= reqMinMeasurement && actMeasurement <= reqMaxMeasurement) {
      discrepancyColor = true;
    } else {
      discrepancyColor = false;
    }
    return discrepancyColor;
  };
  const onImportSourceChange = (e) => {
    setImportSource(e);
    setImportSearchResults([]);
  };

  const onImportSearch = async (query) => {
    setImportLoader(true);
    if (importSource.id === 'POM Library') {
      const res = await dispatch(getAllPom({ loggedInUserId: UserDetails?.userInfo?.userId || '', searchText: query }));
      setImportSearchResults(res?.data?.data || []);
    } else {
      const res = await searchGradingGroupPoms({
        gradingGroupId: selectedGradGroup.value,
        searchText: query,
      });
      setImportSearchResults(res?.data?.data || []);
    }
    setImportLoader(false);
  };

  const onImportSelect = async (selected) => {
    if (!selected?.length) return;
    const pom = selected[0];
    const exists = state.sampleSpecificationList.find((item) => item.code === pom.code);
    if (exists) {
      toasterMsg('POM already added', 'danger');
      typeaheadRef.current?.clear();
      return;
    }
    await dispatch(
      action.importPomToSampleSpec(
        {
          sampleId: smapleId,
          devOrderId,
          gradingGroupId: selectedGradGroup.value,
          pom,
          sourceType: importSource.id === 'POM Library' ? 'pomLibrary' : 'gradingGroup',
        },
        tPS,
      ),
    );
    typeaheadRef.current?.clear();
  };

  const handleSavePom = async (pom) => {
    if (!pom.code?.trim()) {
      toasterMsg('POM code is required', 'danger');
      return;
    }
    if (!pom.description?.trim()) {
      toasterMsg('POM description is required', 'danger');
      return;
    }
    if ((pom.tolMinus && isNaN(Number(pom.tolMinus))) || (pom.tolPlus && isNaN(Number(pom.tolPlus)))) {
      toasterMsg(tPS('DEVELOPMENT_ORDER.BACKEND_SERVICE.TOLERANCE_MEASUREMENT_MUST_BE_NUMBERS'), 'danger');
      return;
    }
    const checkIsZeroExist = state.sampleSpecificationList?.find((val) => val.isZero === true);
    const sampleSizeName = checkIsZeroExist?.sizeName || null;
    const sampleSizeEntry = sampleSizeName && pom.sizes ? pom.sizes[sampleSizeName] : null;
    if (sampleSizeEntry?.revisedMeasurement && isNaN(Number(sampleSizeEntry.revisedMeasurement))) {
      toasterMsg(tPS('DEVELOPMENT_ORDER.BACKEND_SERVICE.TOLERANCE_MEASUREMENT_MUST_BE_NUMBERS'), 'danger');
      return;
    }
    await dispatch(
      action.addManualPomToSampleSpec(
        {
          sampleId: smapleId,
          devOrderId,
          gradingGroupId: selectedGradGroup.value,
          code: pom.code.trim(),
          description: pom.description || '',
          tolMinus: pom.tolMinus || 0,
          tolPlus: pom.tolPlus || 0,
          revisedMeasurement: sampleSizeEntry?.revisedMeasurement || 0,
          comments: pom.comments || '',
        },
        tPS,
      ),
    );
  };

  const onManualPomFieldChange = (rowKey, field, value) => {
    setState((prev) => ({
      ...prev,
      sampleSpecificationList: prev.sampleSpecificationList.map((item) => {
        const matches = item.tempId ? item.tempId === rowKey : item.code === rowKey && item.sourceType === 'manual';
        return matches ? { ...item, [field]: value } : item;
      }),
    }));
  };

  const onDeletePom = async (rowKey) => {
    if (rowKey?.startsWith('temp_')) {
      setState((prev) => ({
        ...prev,
        sampleSpecificationList: prev.sampleSpecificationList.filter((item) => item.tempId !== rowKey),
      }));
      return;
    }
    await dispatch(
      action.deletePomFromSampleSpec(
        {
          sampleId: smapleId,
          devOrderId,
          gradingGroupId: selectedGradGroup.value,
          code: rowKey,
        },
        tPS,
      ),
    );
  };

  const handleQualitySwitch = async (status) => {
    if (status === 'off') {
      setState((prevState) => ({
        ...prevState,
        fraction: 'on',
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        fraction: 'off',
      }));
    }
  };

  const onChangeSize = async (currentSize) => {
    // Kept to avoid breaking references, but we will remove it from UI usage
    if (selectedGradGroup?.value) {
      setValueSelect(currentSize);
    }
  };

  const onChangeGradGroup = async (gradGroup) => {
    setValueSelect({});
    setSelectedGradGroup(gradGroup);
    // Keep parent state in sync so SupplierSpecification receives the selection on tab switch
    props.setSelectedGradGroup?.(gradGroup);
    setState((prev) => ({
      ...prev,
      loading: true,
      conversationType: gradGroup?.conversationType,
      fraction: gradGroup?.fraction,
    }));
    try {
      await dispatch(
        action.getSampleSpecificationByGradingGroup({
          sampleId: smapleId,
          gradingGroupId: gradGroup.value,
        }),
      );
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleAllMeasurement = async (index, ele, keyName) => {
    let changedIndexes = [...userChangedIndexes, index];
    changedIndexes = changedIndexes.filter((v, i, a) => a.indexOf(v) === i);
    setUserChangedIndexes(changedIndexes);

    const { value } = ele.target;

    // Use a regular expression to limit to 3 decimal places
    const regex = /^(\d*\.?\d{0,3})$/;
    if (regex.test(value)) {
      const cloneStat = cloneDeep(state.sampleSpecificationList);
      cloneStat[index][keyName] = value;
      setState((prevState) => ({
        ...prevState,
        sampleSpecificationList: cloneStat,
      }));
    }
  };
  const handleFracMeasurement = async (index, ele, keyName, keyName2, mainKey) => {
    const cloneStat = cloneDeep(state.sampleSpecificationList);
    cloneStat[index][keyName][keyName2] = ele.target.value;
    if (keyName2 === 'mainVal') {
      cloneStat[index][mainKey] = ele.target.value;
    }

    setState((prevState) => ({
      ...prevState,
      sampleSpecificationList: cloneStat,
    }));
  };

  const handleComment = (indices, value, field = 'comments') => {
    const cloneStat = cloneDeep(state.sampleSpecificationList);
    indices.forEach((idx) => {
      cloneStat[idx][field] = value;
    });
    setState((prevState) => ({
      ...prevState,
      sampleSpecificationList: cloneStat,
    }));
  };

  useEffect(() => {
    valueRef.current = {
      sampleSpecificationList: state.sampleSpecificationList,
      conversationType: state.conversationType,
      fraction: state.fraction,
      sizeName: selectedSize,
    };
  }, [state.sampleSpecificationList]);

  useEffect(() => {
    return () => {
      setState((prev) => ({ ...prev, sampleSpecificationList: [] }));
      dispatch(action.unMountSampleSpecSize());
    };
  }, []);

  const handleSaveSpecPomSequence = async (_e, document) => {
    const res = await dispatch(
      action.reorderSampleSpecPomSequence(
        {
          sampleId: smapleId,
          devOrderId,
          gradingGroupId: selectedGradGroup?.value,
          poms: document.poms,
        },
        tPS,
      ),
    );
    if (res) {
      props.onCloseSpecPomRearrange();
      if (selectedGradGroup?.value) {
        dispatch(
          action.getSampleSpecificationByGradingGroup({
            sampleId: smapleId,
            gradingGroupId: selectedGradGroup.value,
          }),
        );
      }
    }
  };

  return (
    <>
      <SpecificationsView
        sizeRange={sizeRange}
        onChangeSize={onChangeSize}
        selectedSize={selectedSize}
        state={state}
        handleQualitySwitch={handleQualitySwitch}
        onSortColumns={onSortColumns}
        headerSortingIcon={headerSortingIcon}
        handleAllMeasurement={handleAllMeasurement}
        callDiffFunction={callDiffFunction}
        handleFracMeasurement={handleFracMeasurement}
        setDiscrepancy={setDiscrepancy}
        handleComment={handleComment}
        isSupplierSpecification={false}
        isSupplierLoggedIn={isSupplierLoggedIn}
        valueSelect={valueSelect}
        SampleDeleteAccess={SampleDeleteAccess}
        SampleAddEditAccess={SampleAddEditAccess}
        onChangeGradGroup={onChangeGradGroup}
        selectedGradGroup={selectedGradGroup}
        gradingGroups={gradingGroupOptions}
        measurementAddEditRights={measurementAddEditRights}
        isProductArchived={productDetails?.isArchived}
        importSource={importSource}
        onImportSourceChange={onImportSourceChange}
        importLoader={importLoader}
        importSearchResults={importSearchResults}
        onImportSearch={onImportSearch}
        onImportSelect={onImportSelect}
        typeaheadRef={typeaheadRef}
        onManualPomFieldChange={onManualPomFieldChange}
        onDeletePom={onDeletePom}
        onSavePom={handleSavePom}
      />
      {props.showSpecPomRearrange && (
        <DragDropSort
          isShow={props.showSpecPomRearrange}
          isToggle={props.onCloseSpecPomRearrange}
          itemList={pomItemsForRearrange}
          saveSequence={handleSaveSpecPomSequence}
          devOrderId={devOrderId}
          sampleId={smapleId}
          fromComp="sampleSpecPom"
        />
      )}
    </>
  );
});

Specifications.displayName = 'Specifications';
export default Specifications;
